// One vessel tick (spec §7): propeller law by hull type, part-load SFC, hotel load, fouling, sea margin, current.
import type { Controls, SimState, SeaEnv, HullType, EngineKind, Phase } from '../types';
import type { ResolvedCraft, SeaEff } from './resolve';
import { seaEff } from './resolve';

/** Speed-power exponents: MAN B&W "Basic Principles of Ship Propulsion" (3.2 / 3.5 / 4.0), small displacement 3.0,
 *  planing 2.5 (Volvo Penta propeller-load exponent for planing-craft ratings). */
export const HULL_EXPONENT: Record<HullType, number> = {
  'displacement-small': 3.0, tanker: 3.2, feeder: 3.5, 'container-large': 4.0, planing: 2.5,
};

/** estimate: relative diesel SFC vs load assembled from ABS/MAN statements (spec §7 and ship notes Q2). */
export const DIESEL_SFC_SHAPE: Array<[number, number]> = [[0.25, 1.12], [0.4, 1.05], [0.55, 1.02], [0.75, 1.0], [0.85, 1.0], [1.0, 1.03]];
/** estimate: simple-cycle gas-turbine part-load penalty (the LM2500 part-load curve is unpublished; ship notes Q2 gap). */
export const GAS_TURBINE_SFC_SHAPE: Array<[number, number]> = [[0.25, 1.45], [0.5, 1.18], [0.75, 1.05], [1.0, 1.0]];
/** Fouling growth: 0.25 % per day in temperate water (British Navy rule quoted by Schultz et al. 2011). */
export const FOULING_PER_DAY = 0.0025;
/** Planing hull pre-planing hump: resistance ×1.6 between 8 and 14 kn (estimate, raised-cosine bump). */
const HUMP_LO = 8, HUMP_HI = 14, HUMP_GAIN = 0.6;
export const CALM_SEA: SeaEnv = { currentKn: 0, seaState: 0, resistancePenalty: 0 };

function interp(curve: Array<[number, number]>, x: number): number {
  if (x <= curve[0][0]) return curve[0][1];
  for (let i = 1; i < curve.length; i++) {
    const [x0, y0] = curve[i - 1], [x1, y1] = curve[i];
    if (x <= x1) return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
  }
  return curve[curve.length - 1][1];
}

/** SFC multiplier by load fraction. */
export function sfcMultiplier(kind: EngineKind, load: number): number {
  return interp(kind === 'gas-turbine' ? GAS_TURBINE_SFC_SHAPE : DIESEL_SFC_SHAPE, load);
}

/** estimate: added resistance 8 % per Douglas sea state, capped at 100 % (MAN: 15 % design margin, head seas 50–100 %). */
export function seaMargin(seaState: number): number {
  return Math.min(1, 0.08 * Math.max(0, seaState));
}

function humpFactor(eff: SeaEff, vKn: number): number {
  if (eff.hullType !== 'planing' || vKn <= HUMP_LO || vKn >= HUMP_HI) return 1;
  return 1 + HUMP_GAIN * 0.5 * (1 - Math.cos((2 * Math.PI * (vKn - HUMP_LO)) / (HUMP_HI - HUMP_LO)));
}

export interface VesselPoint {
  speedKn: number;
  groundKt: number;
  propulsionKw: number;
  fuelKgPerH: number;
  load: number;
  enginesOnline: number;
  nmPerTonne: number;
}

/** Steady state at an ordered speed through the water with this fouling and environment. */
export function vesselPoint(rc: ResolvedCraft, orderedKn: number, fouling: number, env: SeaEnv): VesselPoint {
  const eff = seaEff(rc);
  const n = HULL_EXPONENT[eff.hullType];
  let v = Math.max(eff.minSpeedKn, Math.min(eff.maxSpeedKn, orderedKn));
  const resistanceMul = (1 + fouling) * (1 + seaMargin(env.seaState)) * (1 + env.resistancePenalty);
  let p = eff.designPowerKw * Math.pow(v / eff.designSpeedKn, n) * humpFactor(eff, v) * resistanceMul;
  const pMax = eff.ratedPowerKw * eff.engineCount;
  if (p > pMax) { v *= Math.pow(pMax / p, 1 / n); p = pMax; }
  const online = Math.max(1, Math.min(eff.engineCount, Math.ceil((p + eff.hotelKw) / eff.ratedPowerKw)));
  const load = Math.min(1, (p + eff.hotelKw) / (online * eff.ratedPowerKw));
  const fuel = eff.bsfcKgPerKwh * sfcMultiplier(rc.engine.kind, load) * (p + eff.hotelKw);
  const ground = Math.max(0, v + env.currentKn);
  return { speedKn: v, groundKt: ground, propulsionKw: p, fuelKgPerH: fuel, load, enginesOnline: online, nmPerTonne: fuel > 0 ? (ground * 1000) / fuel : 0 };
}

export function initialState(rc: ResolvedCraft, _controls: Controls): SimState {
  const eff = seaEff(rc);
  return {
    timeS: 0, distanceNm: 0, fuelKg: eff.fuelCapacityKg, massKg: eff.fuelCapacityKg + eff.addedMassKg, altitudeFt: 0,
    tasKt: 0, groundKt: 0, windOrCurrentKt: 0, rpm: rc.engine.idleRpm, load: 0, fuelFlowKgPerH: 0,
    specificRangeNmPerKg: 0, fouling: eff.foulingInitial, seaState: 0, phase: 'cruise', segmentIndex: 0,
    storesJettisoned: false,
  };
}

export function stepVessel(rc: ResolvedCraft, s: SimState, c: Controls, env: SeaEnv, dtS: number): SimState {
  const eff = seaEff(rc);
  if (s.phase === 'ended' || s.fuelKg <= 0) return { ...s, phase: 'ended', fuelKg: Math.max(0, s.fuelKg) };
  const pt = vesselPoint(rc, c.speedKn ?? eff.designSpeedKn, s.fouling, env);
  let dt = dtS;
  let phase: Phase = s.phase;
  const burn = (pt.fuelKgPerH * dtS) / 3600;
  let fuel = s.fuelKg - burn;
  if (fuel <= 0) { dt = burn > 0 ? dtS * (s.fuelKg / burn) : dtS; fuel = 0; phase = 'ended'; }
  const e = rc.engine;
  const rpm = Math.max(e.idleRpm, e.ratedRpm * Math.cbrt(pt.load));
  return {
    ...s,
    timeS: s.timeS + dt,
    distanceNm: s.distanceNm + (pt.groundKt * dt) / 3600,
    fuelKg: fuel,
    massKg: fuel + eff.addedMassKg,
    tasKt: pt.speedKn,
    groundKt: pt.groundKt,
    windOrCurrentKt: env.currentKn,
    rpm,
    load: pt.load,
    fuelFlowKgPerH: pt.fuelKgPerH,
    specificRangeNmPerKg: pt.fuelKgPerH > 0 ? pt.groundKt / pt.fuelKgPerH : 0,
    fouling: s.fouling + (FOULING_PER_DAY * eff.foulingGrowthMul * dt) / 86400,
    seaState: env.seaState,
    phase,
  };
}
